/**
 * Timuntu unified facets drawer:
 *  1. Backdrop click → close drawer (scoped to .tm-unified-facets-scope).
 *  2. Sticky header height computed and exposed as --tm-header-height.
 *  3. Accordion: stop Dawn MenuDrawer submenu/trapFocus on nested filters.
 *  4. Price tiers: exclusive palier buttons → hidden gte/lte inputs.
 *  5. Page scroll lock: overflow-hidden on html+body while the drawer is open
 *     (Dawn only adds overflow-hidden-mobile, which this theme does not style).
 *
 * Règle : ne pas modifier facets.js ; tout se passe ici.
 * Multi-roots : fonctionne sur n'importe quelle section avec la classe
 * .tm-unified-facets-scope (collection, search, etc.).
 */
(function () {
  const ROOT_CLASS = 'tm-unified-facets-scope';
  const BACKDROP_CLASS = 'tm-collection-facets-backdrop';
  const LOCK_ATTR = 'data-tm-facets-scroll-lock';
  let lockedScrollY = 0;

  function getAllRoots(scope) {
    return (scope || document).querySelectorAll('.' + ROOT_CLASS);
  }

  function getRootsInTarget(target) {
    if (!target) return [];
    if (target.classList && target.classList.contains(ROOT_CLASS)) return [target];
    return target.querySelectorAll ? target.querySelectorAll('.' + ROOT_CLASS) : [];
  }

  function getDrawerParts(root) {
    const menuDrawer = root.querySelector('menu-drawer.tm-unified-facets-drawer');
    const details = menuDrawer?.querySelector('details.mobile-facets__disclosure');
    const form = root.querySelector('form#FacetFiltersFormMobile');
    const summary = details?.querySelector('summary.mobile-facets__open-wrapper');
    return { details, form, summary, menuDrawer };
  }

  function ensureBackdrop(form) {
    if (!form || form.querySelector('.' + BACKDROP_CLASS)) return;
    const backdrop = document.createElement('div');
    backdrop.className = BACKDROP_CLASS;
    backdrop.setAttribute('aria-hidden', 'true');
    form.insertBefore(backdrop, form.firstChild);
  }

  function closeDrawer(root) {
    const { details, summary } = getDrawerParts(root);
    if (!details?.hasAttribute('open') || !summary) return;
    summary.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
  }

  function anyFacetsDrawerOpen() {
    return !!document.querySelector('.' + ROOT_CLASS + ' details.mobile-facets__disclosure[open]');
  }

  function otherThemeScrollLocksActive() {
    return !!(
      document.querySelector('cart-drawer.active') ||
      document.querySelector('pickup-availability-drawer[open], pickup-availability-preview[open]')
    );
  }

  function freezeWindowScroll() {
    if (!document.documentElement.hasAttribute(LOCK_ATTR)) return;
    if (window.scrollY === lockedScrollY) return;
    window.scrollTo(0, lockedScrollY);
  }

  function lockPageScroll() {
    if (!document.documentElement.hasAttribute(LOCK_ATTR)) {
      lockedScrollY = window.scrollY;
    }
    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden');
    document.documentElement.setAttribute(LOCK_ATTR, 'true');
    freezeWindowScroll();
  }

  function unlockPageScroll() {
    if (anyFacetsDrawerOpen()) return;
    document.documentElement.removeAttribute(LOCK_ATTR);
    if (otherThemeScrollLocksActive()) return;
    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
  }

  function syncPageScrollLock() {
    if (anyFacetsDrawerOpen()) lockPageScroll();
    else unlockPageScroll();
  }

  function isAtScrollBoundary(el, deltaY) {
    if (!el || !deltaY) return false;
    if (deltaY < 0) return el.scrollTop <= 0;
    return el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
  }

  function onLockedWheel(event) {
    if (!document.documentElement.hasAttribute(LOCK_ATTR)) return;
    const list = event.target.closest('.' + ROOT_CLASS + ' .mobile-facets__list');
    if (list && !isAtScrollBoundary(list, event.deltaY)) return;
    const main = event.target.closest('.' + ROOT_CLASS + ' .mobile-facets__main');
    if (!list && main && !isAtScrollBoundary(main, event.deltaY)) return;
    event.preventDefault();
  }

  function onLockedTouchMove(event) {
    if (!document.documentElement.hasAttribute(LOCK_ATTR)) return;
    if (event.target.closest('.' + ROOT_CLASS + ' .mobile-facets__main, .' + ROOT_CLASS + ' .mobile-facets__list')) {
      return;
    }
    event.preventDefault();
  }

  function bindPageScrollLockOnce() {
    if (document.documentElement.dataset.tmFacetsLockBound === 'true') return;
    document.documentElement.dataset.tmFacetsLockBound = 'true';
    document.addEventListener('wheel', onLockedWheel, { passive: false, capture: true });
    document.addEventListener('touchmove', onLockedTouchMove, { passive: false, capture: true });
    window.addEventListener('scroll', freezeWindowScroll, { passive: true });
  }

  function clearListMaxHeights(root) {
    root.querySelectorAll('.mobile-facets__list').forEach((list) => {
      list.style.maxHeight = '';
    });
  }

  function fitOpenLists(root) {
    const { details } = getDrawerParts(root);
    if (!details?.open) {
      clearListMaxHeights(root);
      return;
    }
    const footer = root.querySelector('.mobile-facets__footer');
    if (!footer) return;
    const footerTop = footer.getBoundingClientRect().top;
    root.querySelectorAll('details.tm-facets-accordion[open] .mobile-facets__list').forEach((list) => {
      const top = list.getBoundingClientRect().top;
      const available = Math.floor(footerTop - top - 8);
      if (available > 64) list.style.maxHeight = available + 'px';
    });
    root.querySelectorAll('details.tm-facets-accordion:not([open]) .mobile-facets__list').forEach((list) => {
      list.style.maxHeight = '';
    });
  }

  function scheduleFitOpenLists(root) {
    requestAnimationFrame(() => {
      fitOpenLists(root);
      requestAnimationFrame(() => fitOpenLists(root));
    });
    setTimeout(() => fitOpenLists(root), 160);
  }

  function bindRoot(root) {
    if (!root || root.dataset.tmFacetsDrawerBound === 'true') return;
    root.dataset.tmFacetsDrawerBound = 'true';

    root.addEventListener('pointerdown', (event) => {
      if (!event.target.classList.contains(BACKDROP_CLASS)) return;
      event.preventDefault();
      closeDrawer(root);
    });

    const { details, form, summary } = getDrawerParts(root);
    if (details && form) {
      ensureBackdrop(form);
      details.addEventListener('toggle', () => {
        if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        if (details.open) ensureBackdrop(form);
        syncPageScrollLock();
        scheduleFitOpenLists(root);
        if (!details.open) clearListMaxHeights(root);
      });
      if (details.open) {
        syncPageScrollLock();
        scheduleFitOpenLists(root);
      }
    }
  }

  // Accordion: native <details> toggle, but Dawn MenuDrawer treats nested
  // summaries as sliding sub-panels (menu-opening + trapFocus). Capture on
  // the summary stops that without preventDefault (details still opens).
  function bindAccordionSummaries(root) {
    root.querySelectorAll('details.tm-facets-accordion').forEach((detailsEl) => {
      const summary = detailsEl.querySelector(':scope > summary.mobile-facets__summary');
      if (summary && summary.dataset.tmAccordionBound !== 'true') {
        summary.dataset.tmAccordionBound = 'true';
        summary.addEventListener(
          'click',
          (event) => {
            event.stopImmediatePropagation();
          },
          true
        );
      }
      if (detailsEl.dataset.tmAccordionToggle !== 'true') {
        detailsEl.dataset.tmAccordionToggle = 'true';
        detailsEl.addEventListener('toggle', () => {
          const s = detailsEl.querySelector(':scope > summary.mobile-facets__summary');
          if (s) s.setAttribute('aria-expanded', detailsEl.open ? 'true' : 'false');
          scheduleFitOpenLists(root);
        });
      }
    });
  }

  function parseMoney(value) {
    if (value == null || value === '') return NaN;
    let s = String(value).replace(/\s/g, '').replace(/'/g, '');
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    if (lastComma >= 0 && lastDot >= 0) {
      if (lastComma > lastDot) {
        s = s.replace(/\./g, '').replace(',', '.');
      } else {
        s = s.replace(/,/g, '');
      }
    } else if (lastComma >= 0) {
      s = s.replace(',', '.');
    }
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : NaN;
  }

  function formatMoney(value, sample) {
    const rounded = Math.round(value);
    if (sample && sample.indexOf(',') !== -1 && sample.indexOf('.') === -1) {
      return String(rounded);
    }
    return String(rounded);
  }

  function dispatchInputAndChange(input) {
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function initPriceFacet(facet) {
    if (!facet || facet.dataset.tmPriceBound === 'true') return;
    const sliderMin = facet.querySelector('.tm-price-slider__thumb--min');
    const sliderMax = facet.querySelector('.tm-price-slider__thumb--max');
    const priceRange = facet.querySelector('price-range');
    if (!sliderMin || !sliderMax || !priceRange) return;

    const textInputs = priceRange.querySelectorAll('input.field__input');
    const minInput = textInputs[0];
    const maxInput = textInputs[1];
    if (!minInput || !maxInput) return;

    facet.dataset.tmPriceBound = 'true';

    const rangeMin = Number(facet.getAttribute('data-range-min')) || 0;
    const rangeMax = Number(facet.getAttribute('data-range-max')) || Number(sliderMax.max) || 0;
    const slider = facet.querySelector('[data-tm-price-slider]');
    const gap = 1;

    function currentMin() {
      return Number(sliderMin.value);
    }
    function currentMax() {
      return Number(sliderMax.value);
    }

    function setFill() {
      if (!slider || rangeMax <= rangeMin) return;
      const minPct = ((currentMin() - rangeMin) / (rangeMax - rangeMin)) * 100;
      const maxPct = ((currentMax() - rangeMin) / (rangeMax - rangeMin)) * 100;
      slider.style.setProperty('--tm-price-min-pct', minPct + '%');
      slider.style.setProperty('--tm-price-max-pct', maxPct + '%');
      sliderMin.setAttribute('aria-valuenow', String(currentMin()));
      sliderMax.setAttribute('aria-valuenow', String(currentMax()));
    }

    function writeTextFields() {
      const sample = maxInput.getAttribute('placeholder') || maxInput.getAttribute('data-max') || '';
      minInput.value = formatMoney(currentMin(), sample);
      maxInput.value = formatMoney(currentMax(), sample);
    }

    function clampFromMin() {
      let min = currentMin();
      let max = currentMax();
      if (min > max - gap) {
        min = Math.max(rangeMin, max - gap);
        sliderMin.value = String(min);
      }
    }

    function clampFromMax() {
      let min = currentMin();
      let max = currentMax();
      if (max < min + gap) {
        max = Math.min(rangeMax, min + gap);
        sliderMax.value = String(max);
      }
    }

    function onSliderInput(which, event) {
      event.stopPropagation();
      if (which === 'min') clampFromMin();
      else clampFromMax();
      writeTextFields();
      setFill();
    }

    function onSliderChange(which, event) {
      event.stopPropagation();
      if (which === 'min') clampFromMin();
      else clampFromMax();
      writeTextFields();
      setFill();
      dispatchInputAndChange(minInput);
      dispatchInputAndChange(maxInput);
    }

    sliderMin.addEventListener('input', (event) => onSliderInput('min', event));
    sliderMax.addEventListener('input', (event) => onSliderInput('max', event));
    sliderMin.addEventListener('change', (event) => onSliderChange('min', event));
    sliderMax.addEventListener('change', (event) => onSliderChange('max', event));

    function syncSlidersFromText() {
      let min = parseMoney(minInput.value);
      let max = parseMoney(maxInput.value);
      if (!Number.isFinite(min)) min = rangeMin;
      if (!Number.isFinite(max)) max = rangeMax;
      min = Math.min(Math.max(min, rangeMin), rangeMax);
      max = Math.min(Math.max(max, rangeMin), rangeMax);
      if (min > max) {
        const swap = min;
        min = max;
        max = swap;
      }
      sliderMin.value = String(Math.round(min));
      sliderMax.value = String(Math.round(max));
      setFill();
    }

    minInput.addEventListener('change', syncSlidersFromText);
    maxInput.addEventListener('change', syncSlidersFromText);
    minInput.addEventListener('input', syncSlidersFromText);
    maxInput.addEventListener('input', syncSlidersFromText);

    setFill();
  }

  function initPriceTiers(facet) {
    if (!facet || facet.dataset.tmPriceTiersBound === 'true') return;
    const gte = facet.querySelector('[data-tm-price-gte]');
    const lte = facet.querySelector('[data-tm-price-lte]');
    const buttons = facet.querySelectorAll('[data-tm-price-tier]');
    if (!gte || !lte || !buttons.length) return;

    facet.dataset.tmPriceTiersBound = 'true';

    function syncDisabled() {
      gte.disabled = gte.value === '';
      lte.disabled = lte.value === '';
    }

    function applyTier(btn, dispatch) {
      buttons.forEach((item) => {
        item.setAttribute('aria-checked', item === btn ? 'true' : 'false');
      });
      gte.value = btn.getAttribute('data-gte') || '';
      lte.value = btn.getAttribute('data-lte') || '';
      syncDisabled();
      if (dispatch) {
        dispatchInputAndChange(gte.disabled ? lte : gte);
        if (!gte.disabled && !lte.disabled) dispatchInputAndChange(lte);
      }
    }

    syncDisabled();

    buttons.forEach((btn, index) => {
      btn.addEventListener('click', () => applyTier(btn, true));
      btn.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
          return;
        }
        event.preventDefault();
        const delta = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
        const next = buttons[(index + delta + buttons.length) % buttons.length];
        next.focus();
        applyTier(next, true);
      });
    });
  }

  function bindPriceSliders(root) {
    root.querySelectorAll('[data-tm-price-facet]').forEach(initPriceFacet);
    root.querySelectorAll('[data-tm-price-tiers]').forEach(initPriceTiers);
  }

  function observeFacets(root) {
    const form = root.querySelector('form#FacetFiltersFormMobile');
    if (!form || form.dataset.tmFacetsObserved === 'true') return;
    form.dataset.tmFacetsObserved = 'true';
    const mo = new MutationObserver(() => {
      bindAccordionSummaries(root);
      bindPriceSliders(root);
      syncPageScrollLock();
      scheduleFitOpenLists(root);
    });
    mo.observe(form, { childList: true, subtree: true });
  }

  let _headerHeightRAF = 0;

  function updateHeaderHeight() {
    const header = document.getElementById('timuntu-header-root');
    if (!header) return;
    const h = header.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--tm-header-height', Math.round(h) + 'px');
  }

  function scheduleHeaderHeightUpdate() {
    cancelAnimationFrame(_headerHeightRAF);
    _headerHeightRAF = requestAnimationFrame(updateHeaderHeight);
  }

  function initRoot(root) {
    if (!root) return;
    bindRoot(root);
    bindAccordionSummaries(root);
    bindPriceSliders(root);
    observeFacets(root);
  }

  function init() {
    bindPageScrollLockOnce();
    updateHeaderHeight();
    getAllRoots().forEach(initRoot);
    syncPageScrollLock();
  }

  window.addEventListener(
    'resize',
    () => {
      scheduleHeaderHeightUpdate();
      getAllRoots().forEach(scheduleFitOpenLists);
    },
    { passive: true }
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    getRootsInTarget(event.target).forEach((root) => {
      delete root.dataset.tmFacetsDrawerBound;
      const form = root.querySelector('form#FacetFiltersFormMobile');
      if (form) delete form.dataset.tmFacetsObserved;
      initRoot(root);
    });
    syncPageScrollLock();
  });
})();
